<script>
import Vue from 'vue'
import VueSession from 'vue-session'

import { getAdminHomeRoute, isAdminSessionAuthorized, isGroupAdmin, isSuperAdminMaster } from '../utils/auth'

// Lazy-loaded page components for code splitting
const Clients = () => import(/* webpackChunkName: "clients" */ '../pages/Company/Client/ListClient.vue')
const ClientGroup = () => import(/* webpackChunkName: "client-group" */ '../pages/Company/ClientGroup/ListClientGroup.vue')
const CompanyGroups = () => import(/* webpackChunkName: "company-groups" */ '../pages/Company/CompanyGroup/CompanyGroups.vue')
const Companies = () => import(/* webpackChunkName: "companies" */ '../pages/Company/Company/ListCompany.vue')
const Equipments = () => import(/* webpackChunkName: "equipments" */ '../pages/Company/Equipment/ListEquipment.vue')
const Incident = () => import(/* webpackChunkName: "incident" */ '../pages/Company/Incident/ListIncident.vue')
const Itinerary = () => import(/* webpackChunkName: "itinerary" */ '../pages/Company/Itineraries/ListItineraries.vue')
const PatrolPoint = () => import(/* webpackChunkName: "patrol-point" */ '../pages/Company/PatrolPoint/PatrolPoint.vue')
const RegisterData = () => import(/* webpackChunkName: "register-data" */ '../pages/Company/RegisterData/RegisterData.vue')
const Sites = () => import(/* webpackChunkName: "sites" */ '../pages/Company/Site/ListSite.vue')
const SiteGroup = () => import(/* webpackChunkName: "site-group" */ '../pages/Company/SiteGroup/ListSiteGroup.vue')
const DailyTrend = () => import(/* webpackChunkName: "dashboard" */ '../pages/Dashboard/DailyTrend/DailyTrend.vue')
const Grafana = () => import(/* webpackChunkName: "grafana" */ '../pages/Grafana/Grafana.vue')
const GeneralSummary = () => import(/* webpackChunkName: "dashboard" */ '../pages/Dashboard/GeneralSummary/GeneralSummary.vue')
const Performance = () => import(/* webpackChunkName: "dashboard" */ '../pages/Dashboard/Performance/Performance.vue')
const IntegrationLogs = () => import(/* webpackChunkName: "integration" */ '../pages/Integration/Log/Log.vue')
const ReportExternalPatrol = () => import(/* webpackChunkName: "integration" */ '../pages/Integration/Reports/PerformancePatrol/PerformancePatrol.vue')
const IntegrationReportSettings = () => import(/* webpackChunkName: "integration" */ '../pages/Integration/Settings/ListReportSettings.vue')
const Log = () => import(/* webpackChunkName: "log" */ '../pages/Log/Log.vue')
const Login = () => import(/* webpackChunkName: "login" */ '../pages/Login/Login.vue')
const Monitor = () => import(/* webpackChunkName: "monitor" */ '../pages/Monitor/Monitor.vue')
const AlertMonitor = () => import(/* webpackChunkName: "alert-monitor" */ '../pages/Monitor/AlertMonitor/AlertMonitor.vue')
const PageNotFound = () => import(/* webpackChunkName: "misc" */ '../pages/PageNotFound/PageNotFound.vue')
const PrivacyPolicy = () => import(/* webpackChunkName: "misc" */ '../pages/PrivacyPolicy/PrivacyPolicy.vue')
const EventsReport = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/Events/EventsReport.vue')
const MisssingCallReport = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/Event/MissedCall/MissedCall.vue')
const IncidentReport = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/Incident/Incident.vue')
const sosAlert = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/PanicAlert/PanicAlert.vue')
const ScanLocalHistory = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/ScanLocalHistory/ScanLocalHistory.vue')
const ScanDataHistory = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/ScanDataHistory/ScanDataHistory.vue')
const PatrolCompleted = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/Patrol/Completed/Completed.vue')
const PatrolIncompleted = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/Patrol/Incompleted/Incompleted.vue')
const PatrolNotVisited = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/Patrol/NotVisited/NotVisited.vue')
const ReportPatrol = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/Patrol/Patrol/Patrol.vue')
const SupervisionPatrolReport = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/Patrol/Supervision/Supervision.vue')
const PatrolPointsReport = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/PatrolPoints/PatrolPoints.vue')
const ReportSettings = () => import(/* webpackChunkName: "reports" */ '../pages/Reports/Settings/ListReportSettings.vue')
const AlertOccurrence = () => import(/* webpackChunkName: "schedule" */ '../pages/Schedule/AlertOccurence/AlertOccurrence.vue')
const SupervisorySchedule = () => import(/* webpackChunkName: "schedule" */ '../pages/Schedule/Supervisory/Schedule.vue')
const VigilantSchedule = () => import(/* webpackChunkName: "schedule" */ '../pages/Schedule/Vigilant/Schedule.vue')
const Settings = () => import(/* webpackChunkName: "settings" */ '../pages/Settings/Settings.vue')
const TimelineFreePatrol = () => import(/* webpackChunkName: "timeline" */ '../pages/Timeline/FreePatrol/TimelineFreePatrol.vue')
const Timeline = () => import(/* webpackChunkName: "timeline" */ '../pages/Timeline/Patrol/Timeline.vue')
const AlertOccurrences = () => import(/* webpackChunkName: "timeline" */ '../pages/Timeline/AlertOccurrences/AlertOccurrences.vue')
const TimeEntry = () => import(/* webpackChunkName: "timeline" */ '../pages/Timeline/TimeEntry/TimeEntry.vue')
const ChangePassword = () => import(/* webpackChunkName: "user" */ '../pages/User/ChangePassword/ChangePassword.vue')
const Colaborator = () => import(/* webpackChunkName: "user" */ '../pages/User/Colaborator/ListColaborator.vue')
const GuardGroup = () => import(/* webpackChunkName: "user" */ '../pages/User/GuardGroup/ListGuardGroup.vue')
const Profile = () => import(/* webpackChunkName: "user" */ '../pages/User/Profile/Profile.vue')
const User = () => import(/* webpackChunkName: "user" */ '../pages/User/User/ListUser.vue')

Vue.use(VueSession)

const session = Vue.prototype.$session

const clearSessionAndRedirectToLogin = () => {
    session.destroy()
    return '/'
}

const checkAdminSession = () => {
    return isAdminSessionAuthorized(session) ? undefined : clearSessionAndRedirectToLogin()
}

// Administração global: somente SUPER_ADMIN_MASTER pode entrar por URL direta.
const checkSuperAdminMasterSession = () => {
    const denied = checkAdminSession()
    if (denied) return denied

    return isSuperAdminMaster(session.get('user')) ? undefined : getAdminHomeRoute()
}

const checkMultiCompanySession = () => {
    const denied = checkAdminSession()
    if (denied) return denied

    const user = session.get('user')
    return isSuperAdminMaster(user) || isGroupAdmin(user) ? undefined : getAdminHomeRoute()
}

// Os relatórios legados não possuem escopo multiempresa. GROUP_ADMIN usa o
// relatório EVENTS, que aplica o escopo e exclui eventos do AlertPort.
const checkSingleCompanyReportSession = () => {
    const denied = checkAdminSession()
    if (denied) return denied

    return isGroupAdmin(session.get('user')) ? getAdminHomeRoute() : undefined
}

const checkLoginRoute = () => {
    if (isAdminSessionAuthorized(session)) {
        return getAdminHomeRoute()
    }

    if (session.exists('user') || session.exists('token')) {
        session.destroy()
    }

    return undefined
}

const routes = [
    { path: '/monitor/partners', component: () => import('../pages/Monitor/PartnerMonitor.vue'), beforeEnter: (_to, _from, next) => { const denied = checkAdminSession(); next(denied || (session.get('user')?.companyUser?.subtype === 'OPERATOR' ? undefined : getAdminHomeRoute())) } },
    {
        path: '/',
        component: Login,
        beforeEnter: (_to, _from, next) => {
            next(checkLoginRoute())
        },
    },
    { path: '/404', component: PageNotFound },
    {
        path: '/privacy-policy',
        component: PrivacyPolicy,
    },
    { path: '*', redirect: '/404' },
    {
        path: '/actions-log',
        name: 'actions-log',
        component: Log,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/companies',
        name: 'companies',
        component: Companies,
        beforeEnter: (_to, _from, next) => {
            next(checkMultiCompanySession())
        },
    },
    {
        path: '/clients',
        name: 'clients',
        component: Clients,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/management/client-groups',
        name: 'client-groups',
        component: ClientGroup,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/management/company-groups',
        name: 'company-groups',
        component: CompanyGroups,
        beforeEnter: (_to, _from, next) => {
            next(checkSuperAdminMasterSession())
        },
    },
    {
        path: '/change-password',
        name: 'change-password',
        component: ChangePassword,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/dashboard/general-summary',
        name: 'dashboard-general-summary',
        component: GeneralSummary,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/dashboard/daily-trend',
        name: 'dashboard-daily-trend',
        component: DailyTrend,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/dashboard/performance',
        name: 'dashboard-performance',
        component: Performance,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/management/guard-groups',
        name: 'guard-groups',
        component: GuardGroup,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/management/itineraries',
        name: 'itineraries',
        component: Itinerary,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/incidents',
        name: 'incidents',
        component: Incident,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/monitor',
        name: 'monitor',
        component: Monitor,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/monitor/surveillance',
        name: 'monitor-surveillance',
        component: Monitor,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/monitor/alert-occurrence',
        name: 'monitor-alert-occurrence',
        component: AlertMonitor,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/grafana',
        name: 'grafana',
        component: Grafana,
        beforeEnter: (_to, _from, next) => {
            next(checkSuperAdminMasterSession())
        },
    },
    {
        path: '/patrol-points',
        name: 'patrol-points',
        component: PatrolPoint,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/profile',
        name: 'profile',
        component: Profile,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/register-data',
        name: 'register-data',
        component: RegisterData,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/settings',
        name: 'settings',
        component: Settings,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/sites',
        name: 'sites',
        component: Sites,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/management/site-groups',
        name: 'site-groups',
        component: SiteGroup,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/management/equipments',
        name: 'equipments',
        component: Equipments,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/schedules/vigilant',
        name: 'vigilant-schedule',
        component: VigilantSchedule,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/schedules/supervisory',
        name: 'supervisory-schedule',
        component: SupervisorySchedule,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/timeline/schedule-patrols',
        name: 'timeline-schedule-patrols',
        component: Timeline,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/timeline/schedule-patrols/:filterParams',
        name: 'timeline-schedule-patrols-params',
        props: true,
        component: Timeline,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/timeline/free-patrols',
        name: 'timeline-free-patrols',
        component: TimelineFreePatrol,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },

    {
        path: '/alert-occurrences',
        name: 'alert-occurrences',
        component: AlertOccurrences,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/time-entries',
        name: 'time-entries',
        component: TimeEntry,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/timeline/free-patrols/:filterParams',
        name: 'timeline-free-patrols-params',
        props: true,
        component: TimelineFreePatrol,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/users',
        name: 'users',
        component: User,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/colaborators',
        name: 'colaborators',
        component: Colaborator,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/patrols',
        name: 'reports-patrols',
        component: ReportPatrol,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/patrols/:filterParams',
        name: 'reports-patrols-params',
        props: true,
        component: ReportPatrol,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/patrols/completed',
        name: 'patrols-completed',
        component: PatrolCompleted,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/patrols/completed/:filterParams',
        name: 'patrols-completed-params',
        props: true,
        component: PatrolCompleted,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/patrols/not-visited',
        name: 'patrols-not-visited',
        component: PatrolNotVisited,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/patrols/not-visited/:filterParams',
        name: 'patrols-not-visited-params',
        props: true,
        component: PatrolNotVisited,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/patrols/incompleted',
        name: 'patrols-incompleted',
        component: PatrolIncompleted,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/patrols/incompleted/:filterParams',
        name: 'patrols-incompleted-params',
        props: true,
        component: PatrolIncompleted,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/events',
        name: 'events-report',
        component: EventsReport,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/sos-alert',
        name: 'sos-alert',
        component: sosAlert,
        beforeEnter: (_to, _from, next) => {
            next(checkSingleCompanyReportSession())
        },
    },
    {
        path: '/reports/scan-local-history',
        name: 'scan-local-history',
        component: ScanLocalHistory,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/scan-data-history',
        name: 'scan-data-history',
        component: ScanDataHistory,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/incident',
        name: 'incident',
        component: IncidentReport,
        beforeEnter: (_to, _from, next) => {
            next(checkSingleCompanyReportSession())
        },
    },
    {
        path: '/reports/missing-call',
        name: 'missing-call',
        component: MisssingCallReport,
        beforeEnter: (_to, _from, next) => {
            next(checkSingleCompanyReportSession())
        },
    },
    {
        path: '/reports/supervision-patrol',
        name: 'supervision-patrol',
        component: SupervisionPatrolReport,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/patrol-points',
        name: 'patrol-points-report',
        component: PatrolPointsReport,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/reports/settings',
        name: 'reports-settings',
        component: ReportSettings,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/integrations/logs',
        name: 'integrations-logs',
        component: IntegrationLogs,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/integrations/reports/summary-patrol',
        name: 'integrations-reports-summary-patrol',
        component: ReportExternalPatrol,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/integrations/reports/settings',
        name: 'integrations-reports-settings',
        component: IntegrationReportSettings,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
    {
        path: '/schedules/alert-occurrence',
        name: 'alert-occurrence',
        component: AlertOccurrence,
        beforeEnter: (_to, _from, next) => {
            next(checkAdminSession())
        },
    },
]

export default routes
</script>
